import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Menu } from "lucide-react";
import {
  LayoutDashboard, FileText, Mail, Users, Building2,
  ClipboardList, BarChart3, Languages, BrainCircuit,
  MessageSquare, Newspaper, Sliders, ScanSearch,
} from "lucide-react";
import { Sidebar } from "@/components/layout/Sidebar";
import type { NavItem } from "@/components/layout/Sidebar";

const STORAGE_KEY = "sidebar_collapsed";

export default function Shell({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(STORAGE_KEY) === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  if (location === "/login") {
    return <>{children}</>;
  }

  if (!user) {
    return <>{children}</>;
  }

  const isAdmin = user.role === "admin";
  const isPlatformAssistant = user.role === "platform_assistant";
  const isWorkspaceAdmin = user.workspaceRole === "workspace_admin";

  const adminNavItems: NavItem[] = [
    { label: "Dashboard", href: "/admin/dashboard", icon: LayoutDashboard },
    { label: "Invitations", href: "/admin/invitations", icon: Mail },
    { label: "Users", href: "/admin/users", icon: Users },
    { label: "Assessments", href: "/admin/assessments", icon: ClipboardList },
    { label: "Workspaces", href: "/admin/workspaces", icon: Building2 },
    { label: "Reports", href: "/admin/reports", icon: BarChart3 },
    { label: "AI Usage", href: "/admin/ai-usage", icon: BrainCircuit },
    { label: "Contacts", href: "/admin/contacts", icon: MessageSquare },
    { label: "Sustainability Scan Leads", href: "/admin/sustainability-scan-leads", icon: ScanSearch },
    { label: "Translations", href: "/admin/translations", icon: Languages },
    { label: "Blog", href: "/blog", icon: Newspaper },
  ];

  const assistantNavItems: NavItem[] = [
    { label: "Dashboard", href: "/assistant/dashboard", icon: LayoutDashboard },
    { label: "Blog", href: "/blog", icon: Newspaper },
    { label: "Translations", href: "/admin/translations", icon: Languages },
  ];

  const workspaceAdminNavItems: NavItem[] = [
    { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    { label: "Assessments", href: "/assessments", icon: FileText },
    { label: "Reports", href: "/reports", icon: BarChart3 },
    { label: "Simulator", href: "/simulator", icon: Sliders },
  ];

  const memberNavItems: NavItem[] = [
    { label: "Assessments", href: "/assessments", icon: FileText },
  ];

  let navItems: NavItem[] = memberNavItems;
  if (isAdmin) navItems = adminNavItems;
  else if (isPlatformAssistant) navItems = assistantNavItems;
  else if (isWorkspaceAdmin) navItems = workspaceAdminNavItems;

  const displayName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email || "User";

  const initials =
    user.firstName && user.lastName
      ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
      : user.email?.charAt(0).toUpperCase() || "U";

  let workspaceLabel = user.companyName || "";
  if (isAdmin) workspaceLabel = "admin";
  else if (isPlatformAssistant) workspaceLabel = "Platform Staff";

  const sidebarWidth = collapsed ? "md:pl-16" : "md:pl-60";

  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <Sidebar
        navItems={navItems}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        workspaceLabel={workspaceLabel}
        displayName={displayName}
        initials={initials}
        showSettings={isWorkspaceAdmin}
      />

      <div className="sticky top-0 z-30 flex h-14 items-center border-b border-slate-200 bg-white px-4 md:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <div className={`flex min-h-screen flex-col transition-[padding] duration-200 ${sidebarWidth}`}>
        <main className="flex-1 px-6 py-6 md:px-8 md:py-8">
          {children}
        </main>
        <footer className="border-t border-border/40 py-6 md:py-0">
          <div className="flex flex-col items-center justify-between gap-4 px-8 md:h-16 md:flex-row">
            <p className="text-xs text-muted-foreground text-center md:text-left">
              {"© 2026 180Pi. Pilot Phase. Built for African SMEs."}
            </p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <a href="#" className="hover:underline">{"Privacy"}</a>
              <a href="#" className="hover:underline">{"Terms"}</a>
              <a href="#" className="hover:underline">Support</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
